/**
 * Platform and motion detection, shared by every complex desktop component.
 *
 * Ports the single-source-of-truth rule from
 * `behaviours/desktop-behaviours.reference.js`: the reference detects the
 * platform ONCE at module scope and everything else reads that one value
 * (`window.dskIsMac`). Nothing re-detects it, because two detections are two
 * chances to disagree about whether the shortcut chip says Cmd or Ctrl.
 */

/* Same test the reference behaviours use, in the same order. */
export const isMac =
  typeof navigator !== 'undefined' &&
  (/Mac|iPhone|iPad/.test(navigator.platform || '') ||
    /Mac/.test(navigator.userAgent || ''));

/** The modifier chip label. Cmd on Mac, Ctrl everywhere else. */
export const MOD_LABEL = isMac ? '⌘' : 'Ctrl ';

/** True when the shortcut modifier for this platform is held. */
export function isModifierHeld(event) {
  return isMac ? event.metaKey : event.ctrlKey;
}

/**
 * Read `prefers-reduced-motion` at call time rather than caching it: the user
 * can flip the OS setting while the tab is open, and a cached value would leave
 * a component waiting out an animation that no longer runs.
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Exit-animation wait, collapsed to zero under reduced motion. */
export function motionDuration(ms) {
  return prefersReducedMotion() ? 0 : ms;
}

/**
 * House number format. Comma-grouped, never the en-ZA space group.
 * Prices on Sleevr are South African Rands and carry an R prefix.
 */
export function group(value) {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** R2,199. Never a dollar sign. */
export function rand(value) {
  return `R${group(value)}`;
}
